import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Printer, Globe, Droplet, LayoutGrid } from "lucide-react";
import { DN_TEMPLATES } from "@/pages/DeliveryNoteTemplates";
import { Watermark } from "@/pages/InvoiceTemplates";

const TEMPLATES = [
  { key: "classic", label: "Klasik" },
  { key: "minimal", label: "Minimalis" },
  { key: "modern", label: "Modern" },
  { key: "corporate", label: "Korporat" },
  { key: "editorial", label: "Editorial" },
];

export default function PrintDN() {
  const { id } = useParams();
  const [dn, setDn] = useState(null);
  const [party, setParty] = useState(null);
  const [owner, setOwner] = useState(null);
  const [settings, setSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  const [lang, setLang] = useState("id");
  const [templateKey, setTemplateKey] = useState("classic");
  const [watermark, setWatermark] = useState("none");
  const [customWm, setCustomWm] = useState("");

  useEffect(() => {
    (async () => {
      const [all, me, s] = await Promise.all([
        api.get("/delivery-notes"),
        api.get("/auth/me"),
        api.get("/settings"),
      ]);
      const found = all.data.find(x => x.id === id);
      setDn(found); setOwner(me.data); setSettings(s.data);
      setTemplateKey(s.data.invoice_template || "classic");
      if (s.data.logo_file_id) {
        const t = localStorage.getItem("token");
        setLogoUrl(`${process.env.REACT_APP_BACKEND_URL}/api/files/${s.data.logo_file_id}?auth=${t}`);
      }
      if (found?.party_id) {
        const parties = await api.get("/parties");
        setParty(parties.data.find(p => p.id === found.party_id));
      }
      const font = s.data.invoice_font || "Outfit";
      if (font !== "Outfit" && !document.getElementById("print-font-" + font.replace(/\s+/g,"-"))) {
        const l = document.createElement("link");
        l.id = "print-font-" + font.replace(/\s+/g,"-");
        l.rel = "stylesheet";
        l.href = `https://fonts.googleapis.com/css2?family=${font.replace(/\s+/g,"+")}:wght@400;500;700&display=swap`;
        document.head.appendChild(l);
      }
    })();
  }, [id]);

  if (!dn) return <div className="p-12 text-forest-400">Memuat…</div>;

  const brandName = settings?.brand_name || owner?.name || "Perusahaan";
  const font = settings?.invoice_font || "Outfit";
  const primary = settings?.primary_color || "#151A18";
  const accent = settings?.accent_color || "#D99C3B";
  const Template = DN_TEMPLATES[templateKey] || DN_TEMPLATES.classic;

  let wmText = watermark === "custom" ? customWm : (watermark === "none" ? "" : watermark);

  return (
    <div className="min-h-screen bg-[#F5F6F4] text-[#151A18]">
      <style>{`@media print { .no-print { display:none !important } body { background:white !important } .print-page { box-shadow:none !important; background:white !important } @page { size:A4; margin:14mm } }`}</style>
      <div className="no-print sticky top-0 z-30 bg-white border-b border-forest-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Globe size={14} className="text-forest-400"/>
            <div className="flex rounded-sm overflow-hidden border border-forest-100">
              <button data-testid="btn-dn-lang-id" onClick={()=>setLang("id")} className={`px-3 py-1 text-xs ${lang==="id"?"bg-[#2D4739] text-white":"bg-white text-forest-400"}`}>ID</button>
              <button data-testid="btn-dn-lang-en" onClick={()=>setLang("en")} className={`px-3 py-1 text-xs ${lang==="en"?"bg-[#2D4739] text-white":"bg-white text-forest-400"}`}>EN</button>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <LayoutGrid size={14} className="text-forest-400"/>
            <Select value={templateKey} onValueChange={setTemplateKey}>
              <SelectTrigger data-testid="sel-dn-template" className="h-8 w-32 text-xs"><SelectValue/></SelectTrigger>
              <SelectContent>{TEMPLATES.map(t => <SelectItem key={t.key} value={t.key} className="text-xs">{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <Droplet size={14} className="text-forest-400"/>
            <Select value={watermark} onValueChange={setWatermark}>
              <SelectTrigger data-testid="sel-dn-watermark" className="h-8 w-32 text-xs"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Tanpa</SelectItem>
                <SelectItem value="DUPLIKAT">DUPLIKAT</SelectItem>
                <SelectItem value="ARSIP">ARSIP</SelectItem>
                <SelectItem value="COPY">COPY</SelectItem>
                <SelectItem value="DRAFT">DRAFT</SelectItem>
                <SelectItem value="custom">Custom…</SelectItem>
              </SelectContent>
            </Select>
            {watermark === "custom" && <Input placeholder="Teks…" value={customWm} onChange={e=>setCustomWm(e.target.value)} className="h-8 w-24 text-xs"/>}
          </div>
          <div className="ml-auto">
            <Button data-testid="btn-dn-print" onClick={()=>window.print()} style={{ backgroundColor: primary }} className="rounded-sm text-white gap-2"><Printer size={14}/> Cetak / PDF</Button>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto p-6">
        <div className="print-page bg-white shadow-md relative overflow-hidden">
          <Watermark text={wmText}/>
          <div className="relative z-10">
            <Template dn={dn} party={party} owner={owner} settings={settings} logoUrl={logoUrl}
                      font={font} primary={primary} accent={accent} brandName={brandName} lang={lang}/>
          </div>
        </div>
      </div>
    </div>
  );
}
